import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Folder, Project, FileItem, UploadTarget } from '../lib/types';
import { listFolders, listProjects, listFilesInFolder } from '../lib/data';
import { createFolder, createProject, deleteFolder, deleteProject } from '../lib/api';
import { Uploader } from './Uploader';
import { FileList } from './FileList';
import { NameDialog } from './NameDialog';
import { Button, Spinner } from './ui';

export function ContainerView({ ownerId, folderId }: { ownerId: string; folderId: string | null }) {
  const [folders, setFolders] = useState<Folder[] | null>(null);
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [files, setFiles] = useState<FileItem[] | null>(null);
  const [dialog, setDialog] = useState<'folder' | 'project' | null>(null);

  const reload = useCallback(async () => {
    const [f, p, fi] = await Promise.all([
      listFolders(ownerId, folderId),
      listProjects(ownerId, folderId),
      listFilesInFolder(ownerId, folderId),
    ]);
    setFolders(f);
    setProjects(p);
    setFiles(fi);
  }, [ownerId, folderId]);

  useEffect(() => {
    setFolders(null);
    setProjects(null);
    setFiles(null);
    reload();
  }, [reload]);

  async function confirmDelete(kind: 'folder' | 'project', id: string, name: string) {
    if (!window.confirm(`Delete ${kind} “${name}” and everything inside it?`)) return;
    if (kind === 'folder') await deleteFolder(id);
    else await deleteProject(id);
    await reload();
  }

  const target: UploadTarget = folderId ? { kind: 'folder', id: folderId } : { kind: 'root' };

  if (folders === null || projects === null || files === null) return <Spinner label="Loading…" />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="secondary" onClick={() => setDialog('folder')}>
          📁 New folder
        </Button>
        <Button variant="secondary" onClick={() => setDialog('project')}>
          🗂 New project
        </Button>
        <Uploader target={target} onUploaded={reload} />
      </div>

      {(folders.length > 0 || projects.length > 0) && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {folders.map((f) => (
            <Link
              key={f.id}
              to={`/folder/${f.id}`}
              className="group rounded-lg border border-slate-200 bg-white p-3 hover:border-brand-300 hover:shadow-sm"
            >
              <div className="flex items-center justify-between">
                <span className="text-2xl">📁</span>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    confirmDelete('folder', f.id, f.name);
                  }}
                  className="invisible text-slate-400 hover:text-red-600 group-hover:visible"
                  aria-label="Delete folder"
                >
                  🗑
                </button>
              </div>
              <p className="mt-2 truncate text-sm font-medium">{f.name}</p>
            </Link>
          ))}
          {projects.map((p) => (
            <Link
              key={p.id}
              to={`/project/${p.id}`}
              className="group rounded-lg border border-slate-200 bg-white p-3 hover:border-brand-300 hover:shadow-sm"
            >
              <div className="flex items-center justify-between">
                <span className="text-2xl">🗂</span>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    confirmDelete('project', p.id, p.name);
                  }}
                  className="invisible text-slate-400 hover:text-red-600 group-hover:visible"
                  aria-label="Delete project"
                >
                  🗑
                </button>
              </div>
              <p className="mt-2 truncate text-sm font-medium">{p.name}</p>
              {p.description && <p className="truncate text-xs text-slate-500">{p.description}</p>}
            </Link>
          ))}
        </div>
      )}

      <div>
        <h2 className="mb-2 text-sm font-semibold text-slate-500">Files</h2>
        <FileList files={files} onChanged={reload} />
      </div>

      {dialog === 'folder' && (
        <NameDialog
          title="New folder"
          label="Folder name"
          onClose={() => setDialog(null)}
          onSubmit={async (name) => {
            await createFolder(name, folderId ?? undefined);
            await reload();
          }}
        />
      )}
      {dialog === 'project' && (
        <NameDialog
          title="New project"
          label="Project name"
          extraField={{ label: 'Description (optional)' }}
          onClose={() => setDialog(null)}
          onSubmit={async (name, desc) => {
            await createProject(name, { description: desc || undefined, folderId: folderId ?? undefined });
            await reload();
          }}
        />
      )}
    </div>
  );
}
